import { useEffect } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { PlayScreen } from './mandala/PlayScreen';
import { DronesGallery } from './routes/DronesGallery';
import { DroneEditor } from './routes/DroneEditor';
import { PublicGallery } from './routes/PublicGallery';
import { PublicDrone } from './routes/PublicDrone';
import { AuthRoute } from './routes/Auth';
import { ActivateRoute, MagicRoute, ResetRoute } from './routes/TokenRoutes';
import { CreditsRoute } from './routes/Credits';
import { AboutRoute } from './routes/About';
import { NotFoundRoute, RouteError } from './routes/ErrorRoutes';
import { useAuth } from './store/authStore';
import { useDrones } from './store/dronesStore';
import { applyTheme, startSettingsServerSync, stopSettingsServerSync, useSettings } from './store/settingsStore';

/** Root layout: theme, session refresh, and store sync on sign-in/out. */
function Root() {
  const theme = useSettings((s) => s.theme);
  const user = useAuth((s) => s.user);
  const status = useAuth((s) => s.status);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system' || typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: light)');
    const fn = () => applyTheme('system');
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [theme]);

  useEffect(() => { void useAuth.getState().refresh(); }, []);

  useEffect(() => {
    if (status === 'unknown') return;
    void useDrones.getState().sync(!!user);
    if (user) void startSettingsServerSync();
    else stopSettingsServerSync();
  }, [user, status]);

  return <Outlet />;
}

const router = createBrowserRouter([
  {
    path: '/', element: <Root />, errorElement: <RouteError />,
    children: [
      { index: true, element: <PlayScreen /> },
      { path: 'drones', element: <DronesGallery /> },
      { path: 'drones/new', element: <DroneEditor /> },
      { path: 'drones/:id', element: <DroneEditor /> },
      { path: 'gallery', element: <PublicGallery /> },
      { path: 'd/:slug', element: <PublicDrone /> },
      { path: 'auth', element: <AuthRoute /> },
      { path: 'activate', element: <ActivateRoute /> },
      { path: 'reset', element: <ResetRoute /> },
      { path: 'magic', element: <MagicRoute /> },
      { path: 'credits', element: <CreditsRoute /> },
      { path: 'about', element: <AboutRoute /> },
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
